import { useModal } from '@billy/design-system'
import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { useQueryParams } from '../../../../../hooks/useQueryParams'
import { ShineConnectionMergeParams, useMergeShineAccounts } from '../../../hooks/useMergeShineAccounts'
import { ShineConnectionValidationParams, useValidateShineConnection } from '../../../hooks/useValidateShineConnection'
import { ShineCompanyProfile } from '../../../types/shineCompanyProfile'
import { ShineUserProfile } from '../../../types/shineUserProfile'

const SHINE_CONNECT_DEFAULT_QUERY = {
  code: undefined,
  state: undefined,
}

interface ContextState {
  handleCloseModal: () => void
  handleMergeAccounts: () => void
  hasMergeFailed?: boolean
  hasMergeSucceeded?: boolean
  hasValidationFailed?: boolean
  hasValidationSucceeded?: boolean
  isMerging: boolean
  isValidating: boolean
  setShineBankAccounts: (accounts: Account[]) => void
  shineBankAccounts: Account[]
  shineOrganizationData?: ShineCompanyProfile
  shineUserData?: ShineUserProfile
}

const ShineConnectModalContext = createContext<ContextState | undefined>(undefined)

interface ShineConnectModalContextProps {
  children: ReactNode
  code: string
  modalId: string
  state: string
}

export const ShineConnectModalContextProvider = ({
  children,
  code,
  modalId,
  state,
}: ShineConnectModalContextProps): ReactElement => {
  const { user } = useCurrentUser()
  const { organization } = useUserOrganization()
  const { isOpen, close } = useModal(modalId)
  const [shineBankAccounts, setShineBankAccounts] = useState<Account[]>([])

  const [, setQueryParams] = useQueryParams({
    defaultQuery: SHINE_CONNECT_DEFAULT_QUERY,
    withDefaultQueryInUrl: false,
  })

  const {
    validate: validateShineAccounts,
    isProcessing: isValidating,
    data,
    isError: hasValidationFailed,
    isSuccess: hasValidationSucceeded,
  } = useValidateShineConnection()

  const {
    update: mergeShineAccounts,
    isProcessing: isMerging,
    isError: hasMergeFailed,
    isSuccess: hasMergeSucceeded,
  } = useMergeShineAccounts()

  const shineOrganizationData = data?.shineOrganization
  const shineUserData = data?.shineUser

  const removeQueryParams = useCallback(() => {
    setQueryParams(SHINE_CONNECT_DEFAULT_QUERY)
  }, [setQueryParams])

  const handleCloseModal = useCallback(() => {
    removeQueryParams()
    close()
  }, [close, removeQueryParams])

  const handleSetShineBankAccounts = useCallback(
    (accounts: Account[]) => {
      setShineBankAccounts(accounts)
    },
    [setShineBankAccounts],
  )

  const handleValidateConnection = useCallback(() => {
    if (!code || !state || !user || !organization || !organization.globalId) {
      return
    }

    const validationPayload: ShineConnectionValidationParams = {
      organizationId: organization.id,
      globalOrgId: organization.globalId,
      globalUserId: user.globalId,
      code,
      state,
    }

    validateShineAccounts(validationPayload)
  }, [code, organization, state, user, validateShineAccounts])

  const handleMergeAccounts = useCallback(() => {
    if (!data || !organization) {
      return
    }

    const mergePayload: ShineConnectionMergeParams = {
      organizationId: organization.id,
      targetProduct: data.targetProduct,
      sourceProduct: data.sourceProduct,
      signature: data.signature,
    }

    mergeShineAccounts(mergePayload)
  }, [data, mergeShineAccounts, organization])

  useEffect(() => {
    if (isOpen) {
      handleValidateConnection()
    }
  }, [isOpen, handleValidateConnection])

  return (
    <ShineConnectModalContext.Provider
      value={{
        handleCloseModal,
        handleMergeAccounts,
        hasMergeFailed,
        hasMergeSucceeded,
        hasValidationFailed,
        hasValidationSucceeded,
        isMerging,
        isValidating,
        setShineBankAccounts: handleSetShineBankAccounts,
        shineBankAccounts,
        shineOrganizationData,
        shineUserData,
      }}
    >
      {children}
    </ShineConnectModalContext.Provider>
  )
}

export const useShineConnectModal = () => {
  const context = useContext(ShineConnectModalContext)

  if (!context) {
    throw new Error('ShineConnectModalContextProvider is missing in the module!')
  }

  return context
}
